import { CheckCircle2, ClipboardList, Hourglass, PencilLine, XCircle } from "lucide-react";
import { formatDistanceToNowStrict } from "date-fns";
import { toneForStatus } from "./EventChip";
import { getStatusConfig } from "./StatusChip";
import type { CalendarEvent, CalendarLegendItem } from "./ActivityCalendar";

export function workLogStatusIcon(status?: string) {
  switch (status) {
    case "approved":
      return <CheckCircle2 />;
    case "pending":
      return <Hourglass />;
    case "rejected":
      return <XCircle />;
    case "changes_requested":
      return <PencilLine />;
    default:
      return <ClipboardList />;
  }
}

interface LogLike {
  id: string | number;
  date?: string;
  hours?: number;
  status?: string;
}

/** Turns work logs into calendar chips: the title comes from `titleOf`, the meta is hours and status. */
export function workLogEvents<T extends LogLike>(
  logs: T[],
  titleOf: (log: T) => string,
  hrefOf?: (log: T) => string,
): CalendarEvent[] {
  return logs
    .filter((l) => l.date)
    .map((l) => ({
      id: l.id,
      date: l.date as string,
      title: titleOf(l),
      meta: `${l.hours ?? 0}h · ${getStatusConfig(l.status ?? "").label}`,
      tone: l.status === "changes_requested" ? "lilac" : toneForStatus(l.status),
      icon: workLogStatusIcon(l.status),
      to: hrefOf?.(l),
    }));
}

export function workLogLegend(logs: LogLike[]): CalendarLegendItem[] {
  const count = (pred: (s?: string) => boolean) => logs.filter((l) => pred(l.status)).length;
  return [
    { label: "Approved", tone: "mint", count: count((s) => s === "approved") },
    { label: "Pending", tone: "peach", count: count((s) => s === "pending") },
    { label: "Changes", tone: "lilac", count: count((s) => s === "changes_requested") },
    { label: "Rejected", tone: "rose", count: count((s) => s === "rejected") },
  ];
}

/** "3 days ago"-style label for a log date, or an empty string when it can't be read. */
export function relativeDay(date?: string) {
  if (!date) return "";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  return formatDistanceToNowStrict(d, { addSuffix: true });
}

/** Latest logs first. */
export function latestLogs<T extends LogLike>(logs: T[], n = 3) {
  return [...logs]
    .filter((l) => l.date)
    .sort((a, b) => new Date(b.date as string).getTime() - new Date(a.date as string).getTime())
    .slice(0, n);
}
